import { useEffect, useState } from 'react'

import { useAuth } from '@/hooks/useAuth'
import { useBiometric } from '@/hooks/useBiometric'

import { useToast } from '@/contexts/ToastContext'

import { biometricUtils } from '@/utils/biometric'

interface UseBiometricAutoLoginParams {
  enabled?: boolean
  onSuccess: () => void
}

export const useBiometricAutoLogin = ({ enabled = true, onSuccess }: UseBiometricAutoLoginParams) => {
  const biometric = useBiometric()
  const { loginWithBiometric, hasStoredRefresh } = useAuth()
  const toast = useToast()

  const [attempted, setAttempted] = useState(false)

  useEffect(() => {
    if (!enabled) return
    if (biometric.isChecking) return
    if (attempted) return

    if (!biometric.available) {
      setAttempted(true)
      return
    }

    const controller = new AbortController()
    const { signal } = controller

    const run = async () => {
      const isBiometricEnabled = await biometricUtils.isEnabled()
      const hasRefresh = await hasStoredRefresh()
      if (signal.aborted) return

      if (!isBiometricEnabled || !hasRefresh) {
        setAttempted(true)
        return
      }

      try {
        await biometric.authenticate(`Entrar com biometria`)
        await loginWithBiometric()
        if (!signal.aborted) onSuccess()
      } catch (error) {
        if (!signal.aborted && !biometric.isUserCancel(error)) {
          toast.error('Não foi possível entrar com biometria.')
        }
      } finally {
        if (!signal.aborted) setAttempted(true)
      }
    }

    run()

    return () => {
      controller.abort()
    }
  }, [enabled, biometric.available, biometric.isChecking, attempted])
}
